import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, IconButton, InputAdornment, CircularProgress, ListItemText, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { ModalStyle, ModalHeader, ModalSearchInput, LocationListItem, ScrollableBox, ActionButton } from './styles';
import { LocationItem, ModalView } from './types';
import { useTranslation } from 'next-i18next';

interface LocationModalProps {
  open: boolean;
  onClose: () => void;
  locationSearchText: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  loadingLocationData: boolean;
  locationData: LocationItem[];
  modalView: ModalView;
  selectedMainOption: LocationItem | null;
  onMainOptionClick: (location: LocationItem) => void;
  onBackToMain: () => void;
  onSelectSubLocation: (parentLocation: LocationItem, subLocation: LocationItem) => void;
  onClearSearch?: () => void;
  onSaveSettings?: () => void;
  isSearchDisabled?: boolean;
  searchValue?: string;
  hasSubLocations?: (location: LocationItem) => Promise<boolean>;
}

const LocationModal: React.FC<LocationModalProps> = ({
  open,
  onClose,
  locationSearchText,
  onSearchChange,
  loadingLocationData,
  locationData,
  modalView,
  selectedMainOption,
  onMainOptionClick,
  onBackToMain,
  onSelectSubLocation,
  onClearSearch = () => {},
  onSaveSettings = () => {},
  isSearchDisabled = false,
  searchValue = '',
  hasSubLocations = async () => true, // ค่าเริ่มต้นคือมีตัวเลือกรอง
}) => {
  const { t } = useTranslation('common');
  
  // สร้าง state ภายในเพื่อเก็บค่าที่กำลังค้นหา
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);
  
  // เก็บสถานะว่าสถานที่ใดมีตัวเลือกรองบ้าง
  const [locationsWithSubs, setLocationsWithSubs] = useState<Record<string | number, boolean>>({});
  
  // ตรวจสอบสถานที่ว่ามีตัวเลือกรองหรือไม่
  useEffect(() => {
    const checkLocationsForSubs = async () => {
      if (modalView === 'main' && locationData.length > 0) {
        console.log('เริ่มตรวจสอบตัวเลือกรอง จำนวน', locationData.length, 'รายการ');
        const result: Record<string | number, boolean> = {};
        
        // ลูปเช็คทีละรายการ
        for (const location of locationData) {
          // กรณีที่ชื่อเป็น "ไม่มีชื่อ" หรือ "ทั้งหมด" จะไม่มีตัวเลือกรอง
          if (location.label === "ไม่มีชื่อ" || location.label === "ทั้งหมด") {
            result[location.value] = false;
            continue;
          }
          
          // ถ้ามี children อยู่แล้ว ไม่ต้องเช็คอีก
          if (location.children && location.children.length > 0) {
            result[location.value] = true;
            continue;
          }
          
          // ถ้าเป็นจังหวัด จะมีตัวเลือกรองเสมอ
          if (locationSearchText === t('location_categories.province')) {
            result[location.value] = true;
            continue;
          }
          
          // เช็คด้วยฟังก์ชันที่ส่งมาจากภายนอก
          try {
            result[location.value] = await hasSubLocations(location);
          } catch (error) {
            console.error('เกิดข้อผิดพลาดในการตรวจสอบตัวเลือกรอง:', error);
            result[location.value] = false;
          }
        }
        
        console.log('ผลการตรวจสอบตัวเลือกรอง:', result);
        setLocationsWithSubs(result);
      }
    };
    
    checkLocationsForSubs();
  }, [locationData, modalView, hasSubLocations, locationSearchText, t]);
  
  // อัพเดท localSearchValue เมื่อ searchValue เปลี่ยน
  useEffect(() => {
    // ตรวจสอบว่า searchValue มีการเปลี่ยนแปลงจริงๆ ก่อนที่จะ update
    if (localSearchValue !== searchValue) {
      setLocalSearchValue(searchValue);
    }
  }, [searchValue]);
  
  // สร้างฟังก์ชันจัดการกับการพิมพ์
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchValue(e.target.value);
    onSearchChange(e);
  };

  // สร้างอ็อบเจ็กต์ "ทั้งหมด" สำหรับแต่ละประเภทสถานที่
  const getSelectAllOption = (parentOption: LocationItem | null): LocationItem => {
    if (!parentOption) return { value: 'all', label: t('all') };
    
    // ถ้าหมวดหมู่คือจังหวัด ให้แสดง "ทุกเขต/ทุกอำเภอ"
    if (locationSearchText === t('location_categories.province')) {
      return {
        value: `all_${parentOption.value}`,
        label: t('all_districts'),
        typeId: parentOption.typeId,
        lat: parentOption.lat,
        lng: parentOption.lng
      };
    }
    
    return {
      value: `all_${parentOption.value}`,
      label: t('all_in').replace('%s', parentOption.label || ''),
      typeId: parentOption.typeId,
      lat: parentOption.lat,
      lng: parentOption.lng
    };
  };

  // ฟังก์ชันตรวจสอบว่าควรแสดงลูกศรหรือไม่
  const shouldShowArrow = (location: LocationItem): boolean => {
    // ถ้าเป็นจังหวัด แสดงลูกศรเสมอ
    if (locationSearchText === t('location_categories.province')) {
      return true;
    }
    
    // กรณีที่ชื่อเป็น "ไม่มีชื่อ" หรือ "ทั้งหมด" ไม่แสดงลูกศร
    if (location.label === "ไม่มีชื่อ" || location.label === "ทั้งหมด") {
      return false;
    }
    
    // ถ้ามี children แสดงลูกศร
    if (location.children && location.children.length > 0) {
      return true;
    }
    
    // ตรวจสอบจากสถานะ locationsWithSubs
    return Boolean(locationsWithSubs[location.value]);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="location-modal-title"
    >
      <Box sx={ModalStyle}>
        <ModalHeader>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {modalView === 'sub' && (
              <IconButton 
                color="inherit" 
                onClick={onBackToMain}
                sx={{ mr: 1 }}
              >
                <KeyboardArrowDownIcon style={{ transform: 'rotate(90deg)' }} />
              </IconButton>
            )}
            <Typography variant="h6" color="white">
              {modalView === 'main' 
                ? locationSearchText 
                : selectedMainOption?.label || ''
              }
            </Typography>
          </Box>
          <IconButton onClick={onClose} color="inherit">
            <CloseIcon />
          </IconButton>
        </ModalHeader>
        
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <ModalSearchInput
            placeholder={`${t('search')} ${modalView === 'main' ? locationSearchText : selectedMainOption?.label || ''}`}
            value={localSearchValue}
            onChange={handleInputChange}
            disabled={false}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#919192' }} />
                </InputAdornment>
              ),
              endAdornment: localSearchValue ? (
                <InputAdornment position="end">
                  <IconButton 
                    onClick={onClearSearch}
                    edge="end"
                    sx={{ color: '#919192' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            fullWidth
            sx={{ mb: 2 }}
          />
          
          {loadingLocationData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexGrow: 1 }}>
              <CircularProgress sx={{ color: '#F4762A' }} />
            </Box>
          ) : (
            <ScrollableBox sx={{ p: 0, flexGrow: 1, mb: 2 }}>
              {locationData.length === 0 ? (
                <Typography sx={{ p: 2, textAlign: 'center', color: '#919192' }}>
                  {t('no_data')}
                </Typography>
              ) : modalView === 'main' ? (
                // แสดงเฉพาะตัวเลือกหลัก
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  {locationData.map((location, index) => (
                    <React.Fragment key={location.value || index}>
                      <LocationListItem 
                        onClick={() => onMainOptionClick(location)}
                      >
                        <ListItemText 
                          primary={location.label || t('no_name')} 
                          primaryTypographyProps={{ 
                            style: { 
                              fontSize: '16px', 
                              fontFamily: 'Prompt, sans-serif',
                              fontWeight: 400,
                              color: '#000000'
                            }
                          }}
                        />
                        {/* แสดงลูกศรเฉพาะเมื่อมีตัวเลือกรอง */}
                        {shouldShowArrow(location) && (
                          <KeyboardArrowRightIcon sx={{ color: '#666666' }} />
                        )}
                      </LocationListItem>
                    </React.Fragment>
                  ))}
                </Box>
              ) : (
                // แสดงตัวเลือกย่อยเมื่อเลือกตัวเลือกหลักแล้ว
                selectedMainOption ? 
                  <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    {selectedMainOption.children && selectedMainOption.children.length > 0 ? (
                      <>
                        {/* เพิ่มตัวเลือก "ทั้งหมด" ที่ด้านบนเฉพาะเมื่อมี children */}
                        <LocationListItem 
                          onClick={() => onSelectSubLocation(selectedMainOption, getSelectAllOption(selectedMainOption))}
                        >
                          <ListItemText 
                            primary={locationSearchText === t('location_categories.province') ? t('all_districts') : t('all_in').replace('%s', selectedMainOption.label || '')} 
                            primaryTypographyProps={{ 
                              style: { 
                                fontSize: '16px', 
                                fontFamily: 'Prompt, sans-serif',
                                fontWeight: 400,
                                color: '#000000'
                              }
                            }}
                          />
                        </LocationListItem>
                        
                        {/* แสดงรายการตัวเลือกย่อย */}
                        {selectedMainOption.children.map((subLocation, index) => (
                          <React.Fragment key={subLocation.value || index}>
                            <LocationListItem 
                              onClick={() => onSelectSubLocation(selectedMainOption, subLocation)}
                            >
                              <ListItemText 
                                primary={subLocation.label || t('no_name')} 
                                primaryTypographyProps={{ 
                                  style: { 
                                    fontSize: '16px', 
                                    fontFamily: 'Prompt, sans-serif',
                                    fontWeight: 400,
                                    color: '#000000'
                                  }
                                }}
                              />
                            </LocationListItem>
                          </React.Fragment>
                        ))}
                      </>
                    ) : loadingLocationData ? (
                      // แสดง loading ถ้ากำลังโหลดข้อมูล
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress sx={{ color: '#F4762A' }} />
                      </Box>
                    ) : (
                      // แสดงข้อความเมื่อไม่มีตัวเลือกย่อย
                      <Typography sx={{ p: 2, textAlign: 'center', color: '#919192' }}>
                        {t('no_sub_data')}
                      </Typography>
                    )}
                  </Box>
                : (
                  <Typography sx={{ p: 2, textAlign: 'center', color: '#919192' }}>
                    {t('no_sub_data')}
                  </Typography>
                )
              )}
            </ScrollableBox>
          )}
          
          {/* เพิ่มปุ่มกลับและบันทึกการตั้งค่าที่ด้านล่าง - แสดงเฉพาะเมื่ออยู่ในโหมด sub */}
          {modalView === 'sub' && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              borderTop: '1px solid #E0E0E0',
              pt: 2,
              pb: 2,
              position: "sticky",
              bottom: 0,
              backgroundColor: "white",
              zIndex: 10
            }}>
              <ActionButton 
                variant="outlined"
                onClick={onBackToMain}
                sx={{ 
                  borderRadius: '25px',
                  flex: 1,
                  color: '#666666',
                  borderColor: '#D3D3D3'
                }}
              >
                {t('back')}
              </ActionButton>
              <ActionButton 
                variant="contained"
                onClick={onSaveSettings}
                sx={{ 
                  borderRadius: '25px',
                  flex: 1,
                  backgroundColor: '#F4762A',
                  '&:hover': {
                    backgroundColor: '#D25E20',
                  }
                }}
              >
                {t('save_settings')}
              </ActionButton>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default LocationModal; 